import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Banner,
  Spinner,
  TextContainer,
  Badge,
  BlockStack,
  InlineStack,
  Modal,
  Text,
} from "@shopify/polaris";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  getCampaign,
  createCampaign,
  updateCampaign,
  getGames,
  getRewardPolicies,
  createVariant,
  updateVariant,
  deleteVariant,
  createRewardPolicy,
  updateRewardPolicy,
  deleteRewardPolicy,
  type Campaign,
  type CampaignScheduleV1,
  type Game,
  type RewardPolicy,
  type CampaignVariant,
} from "../api/campaigns";
import { isShopInstallRedirectError } from "../api/admin-api-client";
import {
  RewardPolicyBuilder,
  type RewardPolicyV1,
} from "../components/RewardPolicyBuilder/RewardPolicyBuilder";
import { VariantManager } from "../components/VariantManager/VariantManager";
import { RuleBuilder } from "../components/TargetingRuleBuilder";
import {
  validateTargetingRule,
  hasActionableTargetingRules,
} from "../components/TargetingRuleBuilder/validation";
import type { TargetingRuleV1 } from "../components/TargetingRuleBuilder";
import { parseWheelTemplateQueryParam } from "../components/games/wheelCatalogTemplates";
import { wheelConfigSchema } from "@gamification/game-types";

function isoToDatetimeLocal(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function buildSchedulePayload(
  startLocal: string,
  endLocal: string
): { ok: true; value: CampaignScheduleV1 } | { ok: false; message: string } {
  const out: CampaignScheduleV1 = {};
  if (startLocal.trim()) {
    const t = new Date(startLocal).getTime();
    if (Number.isNaN(t)) {
      return { ok: false, message: "Start date is not valid" };
    }
    out.start = new Date(startLocal).toISOString();
  }
  if (endLocal.trim()) {
    const t = new Date(endLocal).getTime();
    if (Number.isNaN(t)) {
      return { ok: false, message: "End date is not valid" };
    }
    out.end = new Date(endLocal).toISOString();
  }
  if (out.start && out.end && new Date(out.start) > new Date(out.end)) {
    return { ok: false, message: "End must be on or after start" };
  }
  return { ok: true, value: out };
}

function getPolicyOutcomeCount(policy: RewardPolicy): number {
  try {
    const raw = policy.policy;
    const config = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(config?.outcomes) ? config.outcomes.length : 0;
  } catch {
    return 0;
  }
}

function getVariantUsageCount(policy: RewardPolicy): number {
  return policy._count?.variants ?? 0;
}

const WHEEL_GAME_KEY = "wheel_of_fortune";

/** Same rules as backend `assertCampaignActivationReadiness` / `wheelConfigSchema`. */
function wheelConfigPassesActivation(wheelConfig: unknown): boolean {
  return wheelConfigSchema.safeParse(wheelConfig).success;
}

type ReadinessCheckStatus = "pass" | "fail" | "warning";

type ReadinessCheck = {
  id: string;
  label: string;
  status: ReadinessCheckStatus;
  detail?: string;
};

function computeActivationReadiness(
  isEditMode: boolean,
  variants: CampaignVariant[],
  scheduleStartLocal: string,
  scheduleEndLocal: string,
  targetingRules: TargetingRuleV1 | null
): { checks: ReadinessCheck[]; readyForActive: boolean; activationFailureSummary: string } {
  const scheduleResult = buildSchedulePayload(scheduleStartLocal, scheduleEndLocal);
  const scheduleOk = scheduleResult.ok;
  const scheduleDetail = scheduleResult.ok === false ? scheduleResult.message : undefined;

  const targetingPayload = targetingRules ?? {
    version: "1" as const,
    rule: { type: "and" as const, conditions: [] },
  };
  const actionableTargeting = hasActionableTargetingRules(targetingPayload);

  const checks: ReadinessCheck[] = [];
  const failures: string[] = [];

  if (!isEditMode) {
    checks.push({
      id: "saved",
      label: "Campaign saved with variants",
      status: "fail",
      detail: "Save this campaign first, then add at least one enabled variant before you can activate.",
    });
    failures.push("Save the campaign and add variants before activating.");
    if (actionableTargeting) {
      checks.push({
        id: "targeting",
        label: "Targeting rules need matching visitor data",
        status: "warning",
        detail:
          "Your store must supply information that matches these rules (for example cart or region). Otherwise this campaign may not appear for some visitors.",
      });
    }
    return {
      checks,
      readyForActive: false,
      activationFailureSummary: failures.join(" "),
    };
  }

  const activeVariants = variants.filter((v) => v.status === "ACTIVE");
  const hasActiveVariant = activeVariants.length >= 1;
  checks.push({
    id: "variants",
    label: "At least one enabled variant",
    status: hasActiveVariant ? "pass" : "fail",
    detail: hasActiveVariant ? undefined : "Add a variant or enable an existing one.",
  });
  if (!hasActiveVariant) {
    failures.push("Need at least one enabled variant.");
  }

  const wheelVariants = activeVariants.filter((v) => v.game?.key === WHEEL_GAME_KEY);
  let wheelOk = true;
  if (wheelVariants.length > 0) {
    wheelOk = wheelVariants.every((v) => wheelConfigPassesActivation(v.wheelConfig));
  }
  checks.push({
    id: "wheel",
    label: "Wheel configuration valid (wheel games)",
    status: wheelVariants.length === 0 ? "pass" : wheelOk ? "pass" : "fail",
    detail:
      wheelVariants.length === 0
        ? "No enabled wheel variants."
        : wheelOk
          ? undefined
          : "Each enabled wheel variant needs a valid wheel configuration (segments, rewards, and interaction). Use the wheel editor under Campaign Variants.",
  });
  if (wheelVariants.length > 0 && !wheelOk) {
    failures.push("Fix wheel configuration on every enabled wheel variant.");
  }

  checks.push({
    id: "schedule",
    label: "Schedule valid (or not set)",
    status: scheduleOk ? "pass" : "fail",
    detail: scheduleOk ? undefined : scheduleDetail ?? "Fix start/end dates.",
  });
  if (!scheduleOk) {
    failures.push(scheduleDetail ?? "Invalid schedule.");
  }

  if (actionableTargeting) {
    checks.push({
      id: "targeting",
      label: "Targeting rules need matching visitor data",
      status: "warning",
      detail:
        "Your store must supply information that matches these rules (for example cart or region). Otherwise this campaign may not appear for some visitors.",
    });
  }

  const readyForActive = hasActiveVariant && wheelOk && scheduleOk;
  return {
    checks,
    readyForActive,
    activationFailureSummary: failures.join(" "),
  };
}

const OUTCOME_TYPES = ["message", "discount_code", "free_shipping", "loyalty_points"] as const;

function parseRewardPolicyV1FromApi(policy: RewardPolicy): RewardPolicyV1 {
  try {
    const raw = policy.policy;
    const config = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!config || typeof config !== "object") throw new Error("bad");
    const outcomesRaw = Array.isArray(config.outcomes) ? config.outcomes : [];
    const outcomes: RewardPolicyV1["outcomes"] =
      outcomesRaw.length > 0
        ? outcomesRaw.map((o: Record<string, unknown>) => {
            const t = o.type;
            const type = OUTCOME_TYPES.includes(t as (typeof OUTCOME_TYPES)[number])
              ? (t as (typeof OUTCOME_TYPES)[number])
              : "message";
            const w = o.weight;
            const weight = typeof w === "number" && Number.isFinite(w) ? w : 10;
            return {
              key: typeof o.key === "string" ? o.key : "outcome",
              type,
              weight,
              payload:
                o.payload && typeof o.payload === "object" && !Array.isArray(o.payload)
                  ? (o.payload as Record<string, unknown>)
                  : {},
            };
          })
        : [
            {
              key: "outcome_1",
              type: "message",
              weight: 10,
              payload: { text: "Configure outcomes" },
            },
          ];
    const caps =
      config.caps && typeof config.caps === "object" && !Array.isArray(config.caps)
        ? {
            perCustomerPerDay: (config.caps as { perCustomerPerDay?: number }).perCustomerPerDay,
            perDevicePerDay: (config.caps as { perDevicePerDay?: number }).perDevicePerDay,
          }
        : undefined;
    const b = config.budget;
    const budget =
      b && typeof b === "object" && !Array.isArray(b)
        ? {
            scope: ["DAILY", "WEEKLY", "MONTHLY"].includes(
              (b as { scope?: string }).scope as string
            )
              ? ((b as { scope: "DAILY" | "WEEKLY" | "MONTHLY" }).scope)
              : "DAILY",
            maxGrants: (b as { maxGrants?: number }).maxGrants,
          }
        : undefined;
    return {
      version: "1.0",
      outcomes,
      caps,
      budget,
    };
  } catch {
    return {
      version: "1.0",
      outcomes: [
        {
          key: "outcome_1",
          type: "message",
          weight: 10,
          payload: { text: "Could not read previous policy — edit and save." },
        },
      ],
      caps: { perCustomerPerDay: 3, perDevicePerDay: 5 },
      budget: { scope: "DAILY", maxGrants: 1000 },
    };
  }
}

function CampaignFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = id !== "new";

  const backToCampaigns = useCallback(() => {
    navigate({ pathname: "/campaigns", search: location.search });
  }, [navigate, location.search]);

  /** Strip consumed query param while keeping embedded session params (shop, host, etc.). */
  const navigateAfterWheelTemplateConsumed = useCallback(() => {
    if (!id || id === "new") return;
    const next = new URLSearchParams(location.search);
    next.delete("wheelTemplate");
    const qs = next.toString();
    navigate({ pathname: `/campaigns/${id}`, search: qs ? `?${qs}` : "" }, { replace: true });
  }, [navigate, location.search, id]);
  
  // From Games catalog: game + optional wheel template (lead_capture | instant_discount)
  const preselectedGameId = searchParams.get("gameId");
  const wheelTemplateParam = parseWheelTemplateQueryParam(searchParams.get("wheelTemplate"));

  // Campaign form state
  const [name, setName] = useState("");
  const [placement, setPlacement] = useState("cart");
  const [priority, setPriority] = useState(0);
  const [status, setStatus] = useState<Campaign["status"]>("DRAFT");
  const [scheduleStartLocal, setScheduleStartLocal] = useState("");
  const [scheduleEndLocal, setScheduleEndLocal] = useState("");
  const [targetingRules, setTargetingRules] = useState<TargetingRuleV1 | null>(null);

  // Initialize targeting rules with default structure if null
  const safeTargetingRules: TargetingRuleV1 = targetingRules ?? {
    version: "1",
    rule: {
      type: "and",
      conditions: []
    }
  };

  // Data
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [rewardPolicies, setRewardPolicies] = useState<RewardPolicy[]>([]);
  const [variants, setVariants] = useState<CampaignVariant[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reward policy builder state
  const [isCreatingPolicy, setIsCreatingPolicy] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState("");
  const [newPolicyConfig, setNewPolicyConfig] = useState<any>(null);
  const [creatingPolicy, setCreatingPolicy] = useState(false);

  const [renamePolicyId, setRenamePolicyId] = useState<string | null>(null);
  const [renameNameDraft, setRenameNameDraft] = useState("");
  const [renamingPolicy, setRenamingPolicy] = useState(false);

  const [deletePolicyTarget, setDeletePolicyTarget] = useState<{ id: string; name: string } | null>(
    null
  );
  const [deletingPolicy, setDeletingPolicy] = useState(false);

  const [editPolicyId, setEditPolicyId] = useState<string | null>(null);
  const [editPolicyDraft, setEditPolicyDraft] = useState<RewardPolicyV1 | null>(null);
  const [savingPolicyEdit, setSavingPolicyEdit] = useState(false);

  const loadCampaign = useCallback(async () => {
    if (!isEditMode || !id) return;

    try {
      setLoading(true);
      const data = await getCampaign(id);
      setCampaign(data);
      setName(data.name);
      setPlacement(data.placement);
      setPriority(typeof data.priority === "number" ? data.priority : 0);
      setStatus(data.status);
      setVariants(data.variants || []);
      // Load targeting rules (Phase 5.2)
      if (data.targetingRules && typeof data.targetingRules === "object") {
        setTargetingRules(data.targetingRules as TargetingRuleV1);
      } else {
        setTargetingRules(null);
      }
      const sch = data.schedule;
      if (sch && typeof sch === "object" && !Array.isArray(sch)) {
        const s = sch as CampaignScheduleV1;
        setScheduleStartLocal(s.start ? isoToDatetimeLocal(s.start) : "");
        setScheduleEndLocal(s.end ? isoToDatetimeLocal(s.end) : "");
      } else {
        setScheduleStartLocal("");
        setScheduleEndLocal("");
      }
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load campaign");
    } finally {
      setLoading(false);
    }
  }, [isEditMode, id]);

  const loadData = useCallback(async () => {
    try {
      const [gamesData, policiesData] = await Promise.all([
        getGames(),
        getRewardPolicies(),
      ]);
      setGames(gamesData);
      setRewardPolicies(policiesData);
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load data");
    }
  }, []);

  useEffect(() => {
    loadData();
    loadCampaign();
  }, [loadData, loadCampaign]);

  const handleSave = async () => {
    if (!name || !placement) {
      setError("Please fill in all required fields");
      return;
    }

    setSaving(true);
    setError(null);

    const scheduleResult = buildSchedulePayload(scheduleStartLocal, scheduleEndLocal);
    if (!scheduleResult.ok) {
      setError(scheduleResult.message);
      setSaving(false);
      return;
    }

    if (isEditMode && id && status === "ACTIVE") {
      const r = computeActivationReadiness(
        isEditMode,
        variants,
        scheduleStartLocal,
        scheduleEndLocal,
        targetingRules
      );
      if (!r.readyForActive) {
        setError(
          `Cannot activate yet. ${r.activationFailureSummary} See “Ready to go live?” below for details.`
        );
        setSaving(false);
        return;
      }
    }

    try {
      if (isEditMode && id) {
        await updateCampaign(id, {
          name,
          placement,
          priority,
          status,
          targetingRules,
          schedule: scheduleResult.value,
        });
        setSuccessMessage("Campaign updated successfully");
      } else {
        const validStatus: "DRAFT" | "ACTIVE" | "PAUSED" = 
          status === "ENDED" ? "DRAFT" : status;
        const created = await createCampaign({
          name,
          placement,
          priority,
          status: validStatus,
          targetingRules,
          schedule: scheduleResult.value,
        });
        setSuccessMessage("Campaign created successfully");
        const merged = new URLSearchParams(location.search);
        if (preselectedGameId) merged.set("gameId", preselectedGameId);
        if (wheelTemplateParam) merged.set("wheelTemplate", wheelTemplateParam);
        const qs = merged.toString();
        navigate(
          { pathname: `/campaigns/${created.id}`, search: qs ? `?${qs}` : "" },
          { replace: true }
        );
      }
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleAddVariant = async (variantData: {
    gameId: string;
    gameVersionId: string;
    rewardPolicyId: string;
    weight: number;
    config: any;
    wheelConfig?: unknown;
  }) => {
    if (!isEditMode || !id) {
      setError("Please save the campaign first before adding variants");
      return;
    }

    const variant = await createVariant(id, variantData);
    setVariants([...variants, variant]);
    setSuccessMessage("Variant added successfully");
    await loadCampaign(); // Reload to get full variant data
  };

  const handleUpdateVariant = async (
    variantId: string,
    updates: {
      weight?: number;
      config?: any;
      status?: "ACTIVE" | "DISABLED";
      rewardPolicyId?: string;
    }
  ) => {
    if (!isEditMode || !id) return;

    await updateVariant(id, variantId, updates);
    setSuccessMessage("Variant updated successfully");
    await loadCampaign();
  };

  const handleDeleteVariant = async (variantId: string) => {
    if (!isEditMode || !id) return;

    await deleteVariant(id, variantId);
    setVariants(variants.filter((v) => v.id !== variantId));
    setSuccessMessage("Variant deleted successfully");
  };

  const handleCreateRewardPolicy = async () => {
    if (!newPolicyName || !newPolicyConfig) {
      setError("Please provide a name and configure the policy");
      return;
    }

    setCreatingPolicy(true);
    setError(null);

    try {
      const policy = await createRewardPolicy({
        name: newPolicyName,
        policy: newPolicyConfig,
        expectedValue: 0,
      });
      setRewardPolicies([...rewardPolicies, policy]);
      setSuccessMessage("Reward policy created successfully");
      setIsCreatingPolicy(false);
      setNewPolicyName("");
      setNewPolicyConfig(null);
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to create reward policy");
    } finally {
      setCreatingPolicy(false);
    }
  };

  const openRenamePolicy = (policy: RewardPolicy) => {
    setRenamePolicyId(policy.id);
    setRenameNameDraft(policy.name);
  };

  const handleRenameRewardPolicy = async () => {
    if (!renamePolicyId || !renameNameDraft.trim()) {
      setError("Please enter a policy name");
      return;
    }

    setRenamingPolicy(true);
    setError(null);
    try {
      await updateRewardPolicy(renamePolicyId, { name: renameNameDraft.trim() });
      setRenamePolicyId(null);
      setSuccessMessage("Reward policy renamed");
      await loadData();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to rename reward policy");
    } finally {
      setRenamingPolicy(false);
    }
  };

  const openDeletePolicy = (policy: RewardPolicy) => {
    if (getVariantUsageCount(policy) > 0) {
      return;
    }
    setDeletePolicyTarget({ id: policy.id, name: policy.name });
  };

  const handleConfirmDeletePolicy = async () => {
    if (!deletePolicyTarget) return;

    setDeletingPolicy(true);
    setError(null);
    try {
      await deleteRewardPolicy(deletePolicyTarget.id);
      setDeletePolicyTarget(null);
      setSuccessMessage("Reward policy removed");
      await loadData();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to remove reward policy");
    } finally {
      setDeletingPolicy(false);
    }
  };

  const openEditPolicy = (policy: RewardPolicy) => {
    setEditPolicyId(policy.id);
    setEditPolicyDraft(parseRewardPolicyV1FromApi(policy));
  };

  const closeEditPolicyModal = () => {
    if (!savingPolicyEdit) {
      setEditPolicyId(null);
      setEditPolicyDraft(null);
    }
  };

  const handleSaveEditPolicy = async () => {
    if (!editPolicyId || !editPolicyDraft) return;
    const totalW = editPolicyDraft.outcomes.reduce((s, o) => s + o.weight, 0);
    if (editPolicyDraft.outcomes.length === 0 || totalW <= 0) {
      setError("Add at least one outcome with a positive total weight.");
      return;
    }

    setSavingPolicyEdit(true);
    setError(null);
    try {
      await updateRewardPolicy(editPolicyId, { policy: editPolicyDraft });
      setEditPolicyId(null);
      setEditPolicyDraft(null);
      setSuccessMessage("Reward policy updated");
      await loadData();
      if (isEditMode && id) {
        await loadCampaign();
      }
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to update reward policy");
    } finally {
      setSavingPolicyEdit(false);
    }
  };

  const sessionCount = campaign?._count?.sessions || 0;

  const activationReadiness = useMemo(
    () =>
      computeActivationReadiness(
        isEditMode,
        variants,
        scheduleStartLocal,
        scheduleEndLocal,
        targetingRules
      ),
    [isEditMode, variants, scheduleStartLocal, scheduleEndLocal, targetingRules]
  );

  if (loading) {
    return (
      <Page
        title={isEditMode ? "Edit Campaign" : "Create Campaign"}
        backAction={{ content: "Campaigns", onAction: backToCampaigns }}
      >
        <Card>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title={isEditMode ? "Edit Campaign" : "Create Campaign"}
      backAction={{ content: "Campaigns", onAction: backToCampaigns }}
      primaryAction={{
        content: saving ? "Saving..." : "Save",
        onAction: handleSave,
        loading: saving,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: backToCampaigns,
        },
      ]}
    >
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {successMessage && (
        <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Banner>
      )}

      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text variant="headingLg" as="h2">
                Campaign details
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Name the campaign, choose where it appears, and set priority when several campaigns
                could win the same placement.
              </Text>
            </BlockStack>
            <Card>
              <div style={{ padding: "1rem" }}>
              <FormLayout>
                <TextField
                  label="Campaign Name"
                  value={name}
                  onChange={setName}
                  placeholder="e.g., Cart Page Wheel"
                  autoComplete="off"
                  requiredIndicator
                />

                <Select
                  label="Placement"
                  value={placement}
                  helpText="Multiple Active campaigns can target the same placement. The storefront serves the single winning campaign by priority (below), not by creation order alone."
                  options={[
                    { label: "Cart Page", value: "cart" },
                    { label: "Product Page", value: "product" },
                    { label: "Checkout", value: "checkout" },
                    { label: "Thank You Page", value: "thankyou" },
                    { label: "Homepage", value: "homepage" },
                  ]}
                  onChange={setPlacement}
                  requiredIndicator
                />

                <TextField
                  label="Priority"
                  type="number"
                  value={String(priority)}
                  onChange={(value) => {
                    const trimmed = value.trim();
                    if (trimmed === "" || trimmed === "-") {
                      setPriority(0);
                      return;
                    }
                    const n = parseInt(trimmed, 10);
                    setPriority(Number.isFinite(n) ? n : 0);
                  }}
                  helpText="Whole number. Higher wins when several Active campaigns share this placement. If priority ties, the newer campaign wins; if still tied, the campaign id breaks the tie. This is separate from variant weights inside a campaign."
                  autoComplete="off"
                />

                <Select
                  label="Status"
                  value={status}
                  helpText={
                    isEditMode
                      ? "Active means enabled in the admin (not deleted/paused). The storefront only shows the campaign when it is Active and the optional schedule window is open. Active requires at least one enabled variant; wheel campaigns need a valid wheel config on every enabled variant."
                      : "Active is available after the campaign is saved and has variants. New campaigns start as Draft or Paused. If you set a schedule, the storefront still only shows the campaign while the window is open."
                  }
                  options={
                    isEditMode
                      ? [
                          { label: "Draft", value: "DRAFT" },
                          { label: "Active", value: "ACTIVE" },
                          { label: "Paused", value: "PAUSED" },
                          { label: "Ended", value: "ENDED" },
                        ]
                      : [
                          { label: "Draft", value: "DRAFT" },
                          { label: "Paused", value: "PAUSED" },
                        ]
                  }
                  onChange={(value) => setStatus(value as Campaign["status"])}
                />
              </FormLayout>
            </div>
          </Card>
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text variant="headingLg" as="h2">
                Storefront schedule
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Control when shoppers can see this campaign. Admin status must still be Active for
                anything to run.
              </Text>
            </BlockStack>
            <Card>
            <div style={{ padding: "1rem" }}>
              <Banner tone="info">
                <p>
                  <strong>Active vs schedule:</strong> Active means the campaign is enabled in the admin.
                  The storefront only serves it when the current time is inside the window below (if you set one).
                  Leave both fields empty to allow the campaign whenever it is Active (subject to targeting and other rules).
                </p>
              </Banner>
              <div style={{ marginTop: "1rem" }}>
                <FormLayout>
                  <TextField
                    label="Start (optional)"
                    type="datetime-local"
                    value={scheduleStartLocal}
                    onChange={setScheduleStartLocal}
                    helpText="First instant the experience may run. Values use your browser local time; the API stores UTC."
                    autoComplete="off"
                  />
                  <TextField
                    label="End (optional)"
                    type="datetime-local"
                    value={scheduleEndLocal}
                    onChange={setScheduleEndLocal}
                    helpText="Last instant included in the window. After this moment the campaign is not returned to the storefront."
                    autoComplete="off"
                  />
                </FormLayout>
              </div>
            </div>
          </Card>

          {isEditMode && sessionCount > 0 && (
            <Card>
              <div style={{ padding: "1rem" }}>
                <BlockStack gap="200">
                  <h3 className="Polaris-Text--headingMd">Analytics</h3>
                  <TextContainer>
                    <p>
                      This campaign has <strong>{sessionCount}</strong> total{" "}
                      {sessionCount === 1 ? "session" : "sessions"}.
                    </p>
                  </TextContainer>
                </BlockStack>
              </div>
            </Card>
          )}
          </BlockStack>
        </Layout.Section>

        {isEditMode && (
          <>
            <Layout.Section>
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text variant="headingLg" as="h2">
                    Variants & offer setup
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Connect games and reward policies to this campaign. Weights split traffic between
                    variants; at least one enabled variant is required before activation.
                  </Text>
                </BlockStack>
                <VariantManager
                  campaignId={id!}
                  variants={variants}
                  games={games}
                  rewardPolicies={rewardPolicies}
                  preselectedGameId={preselectedGameId}
                  initialWheelTemplate={wheelTemplateParam}
                  onConsumedInitialWheelTemplate={navigateAfterWheelTemplateConsumed}
                  onAddVariant={handleAddVariant}
                  onUpdateVariant={handleUpdateVariant}
                  onDeleteVariant={handleDeleteVariant}
                  onCampaignReload={loadCampaign}
                />
              </BlockStack>
            </Layout.Section>

            <Layout.Section>
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text variant="headingLg" as="h2">
                    Reward policies
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Shared prize definitions used by variants. Create or reuse policies, then assign
                    them when you add or edit a variant.
                  </Text>
                </BlockStack>
              <Card>
                <div style={{ padding: "1rem" }}>
                  <TextContainer>
                    <p>
                      Reward policies define what customers can win. They are shared across all
                      campaigns and variants—reuse an existing policy when it fits, so your list
                      stays easy to manage.
                    </p>
                    <p style={{ marginTop: "0.5rem" }}>
                      Create new policies below, or pick one when you add a variant.
                    </p>
                    <p style={{ marginTop: "0.5rem" }}>
                      You can remove a policy only when it is not assigned to any variant (this
                      shop or others). If a policy is in use, reassign or delete those variants
                      first — nothing is reassigned for you automatically.
                    </p>
                    <p style={{ marginTop: "0.5rem" }}>
                      Editing a policy&apos;s outcomes or limits updates the <strong>shared</strong>{" "}
                      policy for every campaign and variant that uses it — not only this campaign.
                    </p>
                  </TextContainer>

                  <div style={{ marginTop: "1rem" }}>
                    {rewardPolicies.length > 0 ? (
                      <BlockStack gap="200">
                        {rewardPolicies.map((policy) => {
                          const outcomeCount = getPolicyOutcomeCount(policy);
                          const usage = getVariantUsageCount(policy);
                          return (
                            <Card key={policy.id}>
                              <div style={{ padding: "1rem" }}>
                                <InlineStack align="space-between" blockAlign="start" wrap>
                                  <BlockStack gap="100">
                                    <strong>{policy.name}</strong>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "0.875rem",
                                        color: "var(--p-color-text-secondary)",
                                      }}
                                    >
                                      {outcomeCount} outcome{outcomeCount !== 1 ? "s" : ""} · Used
                                      by {usage} variant{usage !== 1 ? "s" : ""}
                                    </p>
                                  </BlockStack>
                                  <InlineStack gap="200" wrap blockAlign="center">
                                    <Button size="slim" onClick={() => openEditPolicy(policy)}>
                                      Edit outcomes
                                    </Button>
                                    <Button size="slim" onClick={() => openRenamePolicy(policy)}>
                                      Rename
                                    </Button>
                                    {usage === 0 ? (
                                      <Button
                                        size="slim"
                                        tone="critical"
                                        onClick={() => openDeletePolicy(policy)}
                                      >
                                        Remove
                                      </Button>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: "0.8125rem",
                                          color: "var(--p-color-text-secondary)",
                                          maxWidth: "16rem",
                                        }}
                                      >
                                        In use — reassign this policy on every variant that uses it,
                                        then you can remove it.
                                      </span>
                                    )}
                                  </InlineStack>
                                </InlineStack>
                              </div>
                            </Card>
                          );
                        })}
                      </BlockStack>
                    ) : (
                      <Banner>
                        No reward policies found. Create one below to get started.
                      </Banner>
                    )}
                  </div>

                  <div style={{ marginTop: "1.5rem" }}>
                    {!isCreatingPolicy ? (
                      <Button onClick={() => setIsCreatingPolicy(true)}>
                        Create New Reward Policy
                      </Button>
                    ) : (
                      <Card>
                        <div style={{ padding: "1rem" }}>
                        <FormLayout>
                          <TextField
                            label="Policy Name"
                            value={newPolicyName}
                            onChange={setNewPolicyName}
                            placeholder="e.g., Standard Rewards"
                            autoComplete="off"
                          />

                          <RewardPolicyBuilder
                            onChange={setNewPolicyConfig}
                          />

                          <InlineStack gap="200">
                            <Button
                              onClick={handleCreateRewardPolicy}
                              variant="primary"
                              loading={creatingPolicy}
                            >
                              Create Policy
                            </Button>
                            <Button onClick={() => setIsCreatingPolicy(false)}>
                              Cancel
                            </Button>
                          </InlineStack>
                        </FormLayout>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </Card>
              </BlockStack>
            </Layout.Section>
          </>
        )}

        {!isEditMode && (
          <Layout.Section>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text variant="headingLg" as="h2">
                  Variants & offer setup
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Save the campaign first. You will add games and variants here once the campaign exists.
                </Text>
              </BlockStack>
              {preselectedGameId && games.find((g) => g.id === preselectedGameId) ? (
                <Banner tone="info">
                  <p>
                    <strong>Selected game: {games.find((g) => g.id === preselectedGameId)?.name}</strong>
                    {wheelTemplateParam === "lead_capture" && " — Lead Capture wheel template"}
                    {wheelTemplateParam === "instant_discount" && " — Instant Discount wheel template"}
                  </p>
                  <p style={{ marginTop: "0.5rem" }}>
                    Save the campaign first, then add a variant — the wheel will prefill for that template.
                  </p>
                </Banner>
              ) : (
                <Banner>
                  Save the campaign first to add variants and configure rewards.
                </Banner>
              )}
            </BlockStack>
          </Layout.Section>
        )}

        <Layout.Section>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text variant="headingLg" as="h2">
                Targeting
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Show this campaign only to specific visitors. Leave empty to show it to everyone.
              </Text>
            </BlockStack>
            <RuleBuilder
              rule={safeTargetingRules}
              onChange={setTargetingRules}
              onValidate={validateTargetingRule}
              showPreview={true}
            />
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text variant="headingLg" as="h2">
                Status & activation readiness
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Review admin status and what still needs to happen before you can go live with{" "}
                <strong>Active</strong>.
              </Text>
            </BlockStack>

            <Card>
              <div style={{ padding: "1rem" }}>
                <Text variant="headingMd" as="h3">
                  Ready to go live?
                </Text>
                <div style={{ marginTop: "0.5rem" }}>
                  <Text as="p" variant="bodySm" tone="subdued">
                    These checks match what the server requires to set <strong>Active</strong> (variants,
                    wheel, schedule). Targeting is a heads-up only — it does not block activation in the
                    admin.
                  </Text>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <BlockStack gap="300">
                    {activationReadiness.checks.map((c) => (
                      <InlineStack key={c.id} gap="200" blockAlign="start" wrap={false}>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: "1rem",
                            color:
                              c.status === "pass"
                                ? "var(--p-color-text-success)"
                                : c.status === "fail"
                                  ? "var(--p-color-text-critical)"
                                  : "var(--p-color-text-caution)",
                          }}
                          aria-hidden
                        >
                          {c.status === "pass" ? "✓" : c.status === "fail" ? "✗" : "⚠"}
                        </span>
                        <BlockStack gap="100">
                          <Text as="p" variant="bodyMd" fontWeight="semibold">
                            {c.label}
                          </Text>
                          {c.detail && (
                            <Text as="p" variant="bodySm" tone="subdued">
                              {c.detail}
                            </Text>
                          )}
                        </BlockStack>
                      </InlineStack>
                    ))}
                  </BlockStack>
                </div>
              </div>
            </Card>

            {isEditMode && (
              <Card>
                <div style={{ padding: "1rem" }}>
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="headingMd" as="h3">
                        Current status
                      </Text>
                      {status === "ACTIVE" ? (
                        <Badge tone="success">Active</Badge>
                      ) : status === "PAUSED" ? (
                        <Badge tone="warning">Paused</Badge>
                      ) : status === "DRAFT" ? (
                        <Badge>Draft</Badge>
                      ) : (
                        <Badge tone="info">Ended</Badge>
                      )}
                    </InlineStack>
                    <TextContainer>
                      <p style={{ fontSize: "0.875rem", color: "#6d7175" }}>
                        {status === "ACTIVE" &&
                          "This campaign is enabled. Customers only see it on the storefront when the schedule window (if any) is open and other rules pass."}
                        {status === "PAUSED" && "This campaign is paused and not visible to customers"}
                        {status === "DRAFT" && "This campaign is in draft mode and not visible to customers"}
                        {status === "ENDED" && "This campaign has ended and is no longer visible"}
                      </p>
                    </TextContainer>
                  </BlockStack>
                </div>
              </Card>
            )}

            <Card>
              <div style={{ padding: "1rem" }}>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">
                    Reminders
                  </Text>
                  <TextContainer>
                    <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                      <li>Use Draft while you configure, then set Active when the checklist above passes.</li>
                      <li>Variant weights split traffic; one variant is chosen per eligible request.</li>
                      <li>
                        When several campaigns share a placement, the highest <strong>priority</strong>{" "}
                        wins (then newer, then id).
                      </li>
                      <li>
                        Schedule limits storefront visibility; <strong>Active</strong> still means enabled in the admin.
                      </li>
                    </ul>
                  </TextContainer>
                </BlockStack>
              </div>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>

      <Modal
        open={renamePolicyId !== null}
        onClose={() => {
          if (!renamingPolicy) {
            setRenamePolicyId(null);
          }
        }}
        title="Rename reward policy"
        primaryAction={{
          content: "Save",
          onAction: handleRenameRewardPolicy,
          loading: renamingPolicy,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            disabled: renamingPolicy,
            onAction: () => setRenamePolicyId(null),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p style={{ marginBottom: "1rem" }}>
              This only changes the display name. Outcomes and limits are unchanged.
            </p>
          </TextContainer>
          <FormLayout>
            <TextField
              label="Policy name"
              value={renameNameDraft}
              onChange={setRenameNameDraft}
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={deletePolicyTarget !== null}
        onClose={() => {
          if (!deletingPolicy) {
            setDeletePolicyTarget(null);
          }
        }}
        title="Remove reward policy"
        primaryAction={{
          content: "Remove policy",
          destructive: true,
          loading: deletingPolicy,
          onAction: handleConfirmDeletePolicy,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            disabled: deletingPolicy,
            onAction: () => setDeletePolicyTarget(null),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Permanently remove <strong>{deletePolicyTarget?.name ?? ""}</strong>? This only deletes
              the policy definition. It cannot be undone. The server only allows removal when no
              campaign variant (in any campaign) is still assigned to this policy.
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      <Modal
        open={editPolicyId !== null}
        onClose={closeEditPolicyModal}
        title="Edit reward policy"
        size="large"
        primaryAction={{
          content: "Save changes",
          onAction: handleSaveEditPolicy,
          loading: savingPolicyEdit,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            disabled: savingPolicyEdit,
            onAction: closeEditPolicyModal,
          },
        ]}
      >
        <Modal.Section>
          <Banner tone="warning">
            <p>
              This is a <strong>shared</strong> reward policy. Saving applies to every campaign and
              variant that uses it, including other campaigns. It does not change which variant uses
              this policy — only the policy&apos;s definition (outcomes, caps, budget).
            </p>
          </Banner>
        </Modal.Section>
        <Modal.Section>
          {editPolicyDraft && editPolicyId && (
            <RewardPolicyBuilder
              key={editPolicyId}
              initialPolicy={editPolicyDraft}
              onChange={setEditPolicyDraft}
            />
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default CampaignFormPage;
